import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // ✅ NOUVEL IMPORT POUR LA REDIRECTION
import { supabase } from '../../../../services/supabaseClient';
import { User, Calendar, FileText, CheckCircle, Mail, Phone, MoreVertical, X, Shield, Activity, CreditCard, AlertCircle, MessageSquare, Send, FileSignature, ArrowRight, Loader2, Lock } from 'lucide-react'; // ✅ AJOUT DE L'ICÔNE LOCK
// ✅ IMPORT DE LA LIBRAIRIE DE FUSION PDF (pdf-lib)
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const COLUMNS = [
  { id: 'new', title: 'Nouveaux leads', icon: <User size={16}/>, color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { id: 'visit', title: 'Visites planifiées', icon: <Calendar size={16}/>, color: 'bg-purple-50 border-purple-200 text-purple-700' },
  { id: 'docs', title: 'Dossiers en analyse', icon: <FileText size={16}/>, color: 'bg-orange-50 border-orange-200 text-orange-700' },
  { id: 'approved', title: 'Approuvés', icon: <CheckCircle size={16}/>, color: 'bg-green-50 border-green-200 text-green-700' },
];

export default function PipelineBoard() {
  const navigate = useNavigate(); // ✅ INITIALISATION DU NAVIGATEUR
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [candidateDocs, setCandidateDocs] = useState<any[]>([]);
  const [requestingVerification, setRequestingVerification] = useState(false);

  const [modalTab, setModalTab] = useState<'dossier' | 'chat'>('dossier');
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [landlordName, setLandlordName] = useState('Le Propriétaire');

  // ✅ ÉTAT POUR LE CHARGEMENT DU PDF
  const [isGeneratingLease, setIsGeneratingLease] = useState(false);

  useEffect(() => {
    fetchApplications();
    setupUser();
  }, []);

  const setupUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUser(user);
      const { data } = await supabase.from('habitat_profiles').select('full_name').eq('id', user.id).maybeSingle();
      if (data?.full_name) setLandlordName(data.full_name);
    }
  };

  const fetchApplications = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_applications').select(`*, listing:habitat_listings(title, monthly_rent, address, city)`).eq('landlord_id', user.id);
    if (data) setCandidates(data);
    setLoading(false);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const candidateId = e.dataTransfer.getData('candidateId');
    setCandidates((prev) => prev.map((c) => (c.id === candidateId ? { ...c, status: newStatus } : c)));
    await supabase.from('habitat_applications').update({ status: newStatus }).eq('id', candidateId);
  };

  const openDossier = async (candidate: any) => {
    setSelectedCandidate(candidate);
    setModalTab('dossier');
    const { data: docs } = await supabase.from('habitat_documents').select('*').eq('tenant_id', candidate.tenant_id);
    if (docs) setCandidateDocs(docs);
    fetchMessages(candidate.id);
  };

  const fetchMessages = async (appId: string) => {
    const { data } = await supabase.from('habitat_messages').select('*').eq('application_id', appId).order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !selectedCandidate) return;

    const msgContent = newMessage.trim();
    const msg = {
      application_id: selectedCandidate.id,
      sender_id: currentUser.id,
      receiver_id: selectedCandidate.tenant_id,
      content: msgContent
    };

    setMessages([...messages, { ...msg, created_at: new Date().toISOString() }]); 
    setNewMessage('');
    
    await supabase.from('habitat_messages').insert([msg]);

    try {
      if (selectedCandidate.tenant_email) {
        await supabase.functions.invoke('habitat-send-email', {
          body: {
            type: 'new_message',
            email: selectedCandidate.tenant_email,
            data: { senderName: landlordName, listingTitle: selectedCandidate.listing?.title, message: msgContent }
          }
        });
      }
    } catch(err) {
      console.error(err);
    }
    fetchMessages(selectedCandidate.id); 
  };

  const requestVerification = async (paidBy: 'landlord' | 'tenant') => {
    setRequestingVerification(true);
    const { error } = await supabase.from('habitat_applications').update({ verification_status: 'requested', verification_paid_by: paidBy }).eq('id', selectedCandidate.id);
    if (!error) {
      setSelectedCandidate({ ...selectedCandidate, verification_status: 'requested', verification_paid_by: paidBy });
      setCandidates(candidates.map(c => c.id === selectedCandidate.id ? { ...c, verification_status: 'requested', verification_paid_by: paidBy } : c));
    }
    setRequestingVerification(false);
  };

  // ✅ LA FONCTION MAGIQUE DE GÉNÉRATION DU BAIL HYBRIDE (PDF-LIB)
  const generateLeasePDF = async (candidate: any) => {
    setIsGeneratingLease(true);
    try {
      // 1. Créer un nouveau document PDF vide
      const pdfDoc = await PDFDocument.create();
      
      // Polices standards
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontNormal = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      
      // Couleurs
      const teal = rgb(0.078, 0.721, 0.650); // Teal 500
      const slate = rgb(0.058, 0.090, 0.164); // Slate 900
      const green = rgb(0.133, 0.631, 0.290); // Green 600
      const red = rgb(0.7, 0.3, 0.0);
      
      // 2. Ajouter la Page 1 (Annexe Forfeo)
      const page = pdfDoc.addPage([600, 800]);
      const { height } = page.getSize();
      const today = new Date().toLocaleDateString('fr-CA');
      
      // En-tête
      page.drawText("FORFEO HABITAT", { x: 190, y: height - 60, size: 24, font: fontBold, color: teal });
      page.drawText("ANNEXE LÉGALE AU BAIL DU QUÉBEC (TAL)", { x: 135, y: height - 90, size: 14, font: fontBold, color: slate });
      page.drawLine({ start: { x: 50, y: height - 105 }, end: { x: 550, y: height - 105 }, thickness: 1, color: rgb(0.8, 0.8, 0.8) });
      
      page.drawText("La présente annexe est générée par Forfeo Explore Québec. Elle fait partie intégrante du", { x: 90, y: height - 125, size: 10, font: fontItalic, color: slate });
      page.drawText("formulaire officiel du Tribunal administratif du logement (TAL) rattaché ci-après.", { x: 120, y: height - 140, size: 10, font: fontItalic, color: slate });

      // Section 1
      page.drawText("1. IDENTIFICATION DU LOGEMENT", { x: 50, y: height - 190, size: 12, font: fontBold, color: teal });
      page.drawText(`Annonce : ${candidate.listing?.title || 'Logement non spécifié'}`, { x: 50, y: height - 210, size: 10, font: fontNormal, color: slate });
      page.drawText(`Adresse : ${candidate.listing?.address ? `${candidate.listing.address}, ${candidate.listing.city}` : 'À compléter au bail officiel'}`, { x: 50, y: height - 225, size: 10, font: fontNormal, color: slate });
      page.drawText(`Loyer convenu : ${candidate.listing?.monthly_rent || '___'} $ CAD / mois`, { x: 50, y: height - 240, size: 10, font: fontNormal, color: slate });

      // Section 2
      page.drawText("2. LES PARTIES AU CONTRAT", { x: 50, y: height - 280, size: 12, font: fontBold, color: teal });
      page.drawText(`Le Locateur (Propriétaire) : ${landlordName}`, { x: 50, y: height - 300, size: 10, font: fontNormal, color: slate });
      page.drawText(`Le Locataire : ${candidate.tenant_name}`, { x: 50, y: height - 320, size: 10, font: fontNormal, color: slate });
      page.drawText(`Téléphone : ${candidate.tenant_phone}`, { x: 50, y: height - 335, size: 10, font: fontNormal, color: slate });
      page.drawText(`Courriel : ${candidate.tenant_email}`, { x: 50, y: height - 350, size: 10, font: fontNormal, color: slate });

      // Section 3 (Vérification)
      if (candidate.verification_status === 'completed') {
        page.drawRectangle({ x: 50, y: height - 440, width: 500, height: 60, color: rgb(0.94, 0.99, 0.95), borderColor: green, borderWidth: 1 });
        page.drawText("CERTIFICATION FINANCIÈRE FORFEO", { x: 65, y: height - 405, size: 11, font: fontBold, color: green });
        page.drawText(`Ce locataire a complété l'enquête de crédit et revenus (Loi 25) le ${today}.`, { x: 65, y: height - 420, size: 10, font: fontNormal, color: slate });
        page.drawText(`Score de crédit : ${candidate.credit_score} | Revenus : Vérifiés conformes par connexion bancaire.`, { x: 65, y: height - 435, size: 10, font: fontNormal, color: slate });
      } else {
        page.drawText(`Le dossier financier de ce locataire n'a pas été certifié numériquement via Forfeo.`, { x: 50, y: height - 405, size: 10, font: fontItalic, color: red });
      }

      // Section Signatures
      page.drawText("3. SIGNATURES (ENTENTE DE PRINCIPE)", { x: 50, y: height - 490, size: 12, font: fontBold, color: slate });
      page.drawLine({ start: { x: 50, y: height - 540 }, end: { x: 250, y: height - 540 }, thickness: 1, color: slate });
      page.drawText(`Locateur : ${landlordName}`, { x: 50, y: height - 555, size: 10, font: fontNormal, color: slate });
      page.drawText("Date : _________________", { x: 50, y: height - 570, size: 10, font: fontNormal, color: slate });

      page.drawLine({ start: { x: 300, y: height - 540 }, end: { x: 500, y: height - 540 }, thickness: 1, color: slate });
      page.drawText(`Locataire : ${candidate.tenant_name}`, { x: 300, y: height - 555, size: 10, font: fontNormal, color: slate });
      page.drawText("Date : _________________", { x: 300, y: height - 570, size: 10, font: fontNormal, color: slate });

      page.drawText("Document généré de façon sécurisée par Forfeo Explore Québec © 2026", { x: 130, y: 50, size: 8, font: fontNormal, color: rgb(0.6, 0.6, 0.6) });

      // 3. FUSION AVEC LE VRAI BAIL DU TAL (RECHERCHE INTELLIGENTE)
      try {
        // Essai 1 : Dans le dossier 'templates'
        let publicUrl = supabase.storage.from('habitat_public').getPublicUrl('templates/T-15.01R3_FR_005_003.pdf').data.publicUrl;
        let res = await fetch(publicUrl);
        
        // Essai 2 : Si échec, on cherche à la racine du bucket (au cas où tu l'as mis là)
        if (!res.ok) {
          publicUrl = supabase.storage.from('habitat_public').getPublicUrl('T-15.01R3_FR_005_003.pdf').data.publicUrl;
          res = await fetch(publicUrl);
        }

        if (!res.ok) {
          throw new Error("Fichier introuvable sur le serveur.");
        }

        // On télécharge et on fusionne
        const talPdfBytes = await res.arrayBuffer();
        const talDoc = await PDFDocument.load(talPdfBytes);
        const copiedPages = await pdfDoc.copyPages(talDoc, talDoc.getPageIndices());
        copiedPages.forEach((p) => pdfDoc.addPage(p));
        
      } catch (err) {
        console.warn("Erreur de fusion PDF:", err);
        alert("Attention: Le PDF officiel du TAL n'a pas pu être fusionné. Assure-toi que le fichier 'T-15.01R3_FR_005_003.pdf' est bien présent dans ton bucket 'habitat_public' sur Supabase !");
      }

      // 4. SAUVEGARDER ET TÉLÉCHARGER LE RÉSULTAT FINAL
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `Bail_Complet_Forfeo_${candidate.tenant_name.replace(/\s+/g, '_')}.pdf`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // 5. SAUVEGARDE CLOUD DANS SUPABASE
      try {
        const filePath = `leases/${candidate.id}_${Date.now()}.pdf`;
        const { error: uploadError } = await supabase.storage.from('habitat_public').upload(filePath, blob, { contentType: 'application/pdf' });
        
        if (!uploadError) {
          const { data } = supabase.storage.from('habitat_public').getPublicUrl(filePath);
          await supabase.from('habitat_leases').insert([{
            application_id: candidate.id,
            landlord_id: currentUser.id,
            tenant_id: candidate.tenant_id,
            file_name: fileName,
            file_url: data.publicUrl,
            status: 'generated'
          }]);
        }
      } catch (dbError) {
        console.error("Sauvegarde cloud échouée:", dbError);
      }

    } catch (error) {
      console.error("Erreur génération PDF:", error);
      alert("Une erreur est survenue lors de la génération du bail.");
    } finally {
      setIsGeneratingLease(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Chargement...</div>;

  return (
    <div className="h-full flex flex-col relative">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">Pipeline de Candidatures</h2>
          <p className="text-slate-500 text-sm font-medium">Glissez et déposez pour faire avancer les dossiers.</p>
        </div>
      </div>

      <div className="flex-1 flex gap-6 overflow-x-auto pb-6 items-start scroll-smooth">
        {COLUMNS.map((column) => (
          <div key={column.id} className="flex flex-col flex-shrink-0 w-[340px] bg-white rounded-3xl border border-slate-200 h-full min-h-[600px] shadow-sm" onDragOver={(e)=>e.preventDefault()} onDrop={(e) => handleDrop(e, column.id)}>
            <div className={`p-5 rounded-t-3xl border-b flex items-center justify-between ${column.color}`}>
              <div className="flex items-center gap-2 font-black text-sm tracking-tight">{column.icon} {column.title}</div>
              <span className="bg-white/60 px-2.5 py-0.5 rounded-full text-xs font-black shadow-sm">{candidates.filter(c => c.status === column.id).length}</span>
            </div>
            <div className="p-4 flex-1 flex flex-col gap-4 bg-slate-50/50 rounded-b-3xl">
              {candidates.filter((c) => c.status === column.id).map((candidate) => (
                <div key={candidate.id} draggable onDragStart={(e) => { e.dataTransfer.setData('candidateId', candidate.id); }} className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 cursor-grab active:cursor-grabbing hover:shadow-lg hover:border-teal-300 transition-all group">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-black text-slate-900">{candidate.tenant_name}</h4>
                    {candidate.status === 'approved' && <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>}
                  </div>
                  <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-2 rounded-lg font-medium">
                    <span className="text-teal-600 font-bold block mb-1">Pour : {candidate.listing?.title}</span>
                  </div>
                  <button onClick={() => openDossier(candidate)} className="w-full text-xs font-black uppercase tracking-widest text-slate-600 hover:text-white transition-colors bg-slate-100 hover:bg-teal-600 py-2.5 rounded-xl flex justify-center gap-2 items-center">
                    Voir le dossier <ArrowRight size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* MODAL DOSSIER & CHAT */}
      {selectedCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-[2rem] w-full max-w-2xl h-[85vh] shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
            
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h3 className="text-2xl font-black text-slate-900">{selectedCandidate.tenant_name}</h3>
                <p className="text-sm font-bold text-teal-600">{selectedCandidate.listing?.title}</p>
              </div>
              <button onClick={() => setSelectedCandidate(null)} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-900 shadow-sm border border-slate-200"><X size={20}/></button>
            </div>

            <div className="flex px-6 pt-4 gap-4 border-b border-slate-100 bg-white">
              <button onClick={() => setModalTab('dossier')} className={`pb-3 text-sm font-black uppercase tracking-widest transition-colors ${modalTab === 'dossier' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-900'}`}>Dossier Complet</button>
              <button onClick={() => setModalTab('chat')} className={`pb-3 text-sm font-black uppercase tracking-widest transition-colors flex items-center gap-2 ${modalTab === 'chat' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-900'}`}>
                <MessageSquare size={16}/> Messages
              </button>
            </div>
            
            {modalTab === 'dossier' && (
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                
                {/* BOUTON MAGIQUE DE GÉNÉRATION DU BAIL */}
                {selectedCandidate.status === 'approved' && (
                  <div className="bg-gradient-to-r from-green-500 to-emerald-400 rounded-2xl p-6 text-white shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <h4 className="text-lg font-black mb-1 flex items-center gap-2"><CheckCircle size={20}/> Dossier Approuvé !</h4>
                      <p className="text-sm font-medium text-green-50">Générez l'Annexe Légale pour finaliser l'entente.</p>
                    </div>
                    <button 
                      onClick={() => generateLeasePDF(selectedCandidate)} 
                      disabled={isGeneratingLease}
                      className="bg-white text-green-600 px-6 py-3 rounded-xl font-black uppercase tracking-widest shadow-md hover:scale-105 transition-transform flex items-center gap-2 w-full md:w-auto justify-center disabled:opacity-50 disabled:scale-100"
                    >
                      {isGeneratingLease ? <Loader2 size={18} className="animate-spin" /> : <FileSignature size={18}/>}
                      {isGeneratingLease ? 'Génération...' : 'Générer le Bail'}
                    </button>
                  </div>
                )}

                <div className="bg-slate-900 rounded-2xl p-6 text-white relative overflow-hidden">
                  <h4 className="text-sm font-black uppercase tracking-widest text-teal-400 mb-4 flex items-center gap-2"><Activity size={16} /> Enquête de crédit & revenus</h4>
                  {(!selectedCandidate.verification_status || selectedCandidate.verification_status === 'none') && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <button onClick={() => requestVerification('landlord')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl text-left"><p className="font-bold mb-1">Je paie (24.99$)</p></button>
                      <button onClick={() => requestVerification('tenant')} className="bg-teal-500 hover:bg-teal-600 p-4 rounded-xl text-left"><p className="font-bold mb-1">Locataire paie</p></button>
                    </div>
                  )}
                  {selectedCandidate.verification_status === 'requested' && <p className="text-amber-400 font-bold">En attente de l'autorisation du locataire...</p>}
                  {selectedCandidate.verification_status === 'completed' && (
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-white/10 p-4 rounded-xl"><p className="text-xs text-slate-400 uppercase">Score</p><p className="text-3xl font-black text-green-400">{selectedCandidate.credit_score}</p></div>
                      <div className="bg-white/10 p-4 rounded-xl"><p className="text-xs text-slate-400 uppercase">Revenus</p><p className="text-green-400 font-bold mt-2 flex items-center justify-center gap-1"><CheckCircle size={18}/> Vérifiés</p></div>
                    </div>
                  )}

                  {/* ✅ NOUVEAU BOUTON : ACCÈS AU COFFRE-FORT NUMÉRIQUE */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <button 
                      onClick={() => navigate(`/habitat/vault-viewer/${selectedCandidate.tenant_id}`)}
                      className="w-full bg-slate-800 hover:bg-slate-700 text-white py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-3 border border-slate-600"
                    >
                      <Lock size={18} className="text-teal-400" />
                      Ouvrir le coffre-fort des documents
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h4 className="text-xs font-black uppercase text-slate-400 mb-4">Informations Locataire</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="block text-slate-500">Téléphone</span><span className="font-bold">{selectedCandidate.tenant_phone}</span></div>
                    <div><span className="block text-slate-500">Courriel</span><span className="font-bold">{selectedCandidate.tenant_email}</span></div>
                    <div className="col-span-2"><span className="block text-slate-500">Message initial</span><span className="font-medium bg-slate-50 p-3 rounded-lg block mt-1">{selectedCandidate.message}</span></div>
                  </div>
                </div>
              </div>
            )}

            {modalTab === 'chat' && (
              <div className="flex flex-col flex-1 bg-slate-50 overflow-hidden">
                <div className="flex-1 p-6 overflow-y-auto space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center text-slate-400 mt-10 font-medium">Envoyez le premier message pour organiser une visite ou signer le bail.</div>
                  ) : (
                    messages.map((msg, idx) => (
                      <div key={idx} className={`flex flex-col ${msg.sender_id === currentUser?.id ? 'items-end' : 'items-start'}`}>
                        <div className={`max-w-[80%] p-4 rounded-2xl ${msg.sender_id === currentUser?.id ? 'bg-teal-500 text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`}>
                          <p className="text-sm font-medium">{msg.content}</p>
                        </div>
                        <span className="text-[10px] text-slate-400 mt-1 font-bold">{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-4 bg-white border-t border-slate-200">
                  <form onSubmit={sendMessage} className="flex gap-2 relative">
                    <input type="text" value={newMessage} onChange={(e)=>setNewMessage(e.target.value)} placeholder="Écrivez un message au locataire..." className="flex-1 bg-slate-100 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 pr-12" />
                    <button type="submit" disabled={!newMessage.trim()} className="absolute right-2 top-2 bottom-2 bg-teal-500 text-white w-10 rounded-lg flex items-center justify-center hover:bg-teal-600 disabled:opacity-50 transition-colors">
                      <Send size={16} />
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
